import React, { useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { ErrorMessage } from "../components/ErrorMessage";
import { Loading } from "../components/Loading";
import {
  getLastWorkplaceId,
  setLastWorkplaceId,
  useMyWorkplaces,
} from "../hooks/useMyWorkplaces";

const Page = styled.main`
  min-height: 100vh;
  padding: 2rem 1rem;
  background: #f4f5f7;
`;

const Content = styled.div`
  width: 100%;
  max-width: 48rem;
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  line-height: 1.3;
  font-weight: 700;
  color: #172b4d;
  margin: 0;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  gap: 0.75rem;
`;

const WorkplaceButton = styled.button`
  min-height: 6rem;
  border: 1px solid #dfe1e6;
  border-radius: 6px;
  background: #ffffff;
  padding: 1rem;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(9, 30, 66, 0.08);

  &:hover {
    border-color: #36b37e;
    box-shadow: 0 4px 12px rgba(9, 30, 66, 0.12);
  }
`;

const WorkplaceName = styled.div`
  color: #172b4d;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const WorkplaceMeta = styled.div`
  color: #5e6c84;
  font-size: 0.8125rem;
`;

const Empty = styled.div`
  background: #ffffff;
  border: 1px solid #dfe1e6;
  border-radius: 6px;
  color: #5e6c84;
  padding: 1.25rem;
  line-height: 1.6;
`;

export const WorkplaceSelectPage: React.FC = () => {
  const navigate = useNavigate();
  const { workplaces, fetching, error } = useMyWorkplaces();

  useEffect(() => {
    if (fetching || workplaces.length === 0) return;

    const lastWorkplaceId = getLastWorkplaceId();
    const restoreWorkplace =
      workplaces.find((workplace) => workplace.workplaceId === lastWorkplaceId) ??
      (workplaces.length === 1 ? workplaces[0] : null);

    if (restoreWorkplace) {
      navigate(`/workplaces/${restoreWorkplace.workplaceId}/shifts`, {
        replace: true,
      });
    }
  }, [fetching, navigate, workplaces]);

  if (fetching) {
    return <Loading message="職場を読み込んでいます..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="職場の読み込みに失敗しました"
        message={error.message}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <Page>
      <Content>
        <Header>
          <Title>職場を選択</Title>
        </Header>
        {workplaces.length === 0 ? (
          <Empty>
            所属している職場がありません。ネイティブアプリで職場を作成または招待を完了してください。
          </Empty>
        ) : (
          <List>
            {workplaces.map((workplace) => (
              <WorkplaceButton
                key={workplace.workplaceUserId}
                type="button"
                onClick={() => {
                  setLastWorkplaceId(workplace.workplaceId);
                  navigate(`/workplaces/${workplace.workplaceId}/shifts`);
                }}
              >
                <WorkplaceName>{workplace.workplace.name}</WorkplaceName>
                <WorkplaceMeta>{workplace.name}</WorkplaceMeta>
              </WorkplaceButton>
            ))}
          </List>
        )}
      </Content>
    </Page>
  );
};
